import { CampaignConfigSchema, CampaignType } from '../types';
import { campaignTypeSpecs } from './campaignTypes';
import { CampaignPolicy, makePolicy } from './policy';

/**
 * One shared object per campaign type, built once from the spec table.
 *
 * It has to be built once: `resolvePolicy` is called during render, so a fresh
 * object each time would give the policy a new identity on every render and
 * silently break every `useMemo`/`useCallback`/`useEffect` that depends on it.
 * That is exactly how MessagesField ended up refetching calendars on every
 * keystroke.
 */
const registry = Object.fromEntries(
  Object.entries(campaignTypeSpecs).map(
    ([type, spec]) => [type, makePolicy(type as CampaignType, spec)],
  ),
) as Record<CampaignType, CampaignPolicy>;

export { registry as policyRegistry };

/** The policy for a campaign type. Throws rather than returning a broken default. */
export function policyFor(type: CampaignType | string): CampaignPolicy {
  const policy = registry[type as CampaignType];
  if (!policy) throw new Error(`No policy for campaign type "${type}"`);
  return policy;
}

export const resolvePolicy = (config: Pick<CampaignConfigSchema, 'type'>): CampaignPolicy =>
  policyFor(config.type as CampaignType);
